import { Injectable } from '@angular/core';
import { PlatformService } from 'src/app/services/platform/platform.service';

import { environment } from 'src/environments/environment';

// Models
import { CallIssueType } from 'src/app/models/call-issue/call-issue';
import { PeerMetrics } from 'src/app/models/peer-metrics/peer-metrics';
import { PeerStats } from 'src/app/models/peer-stats/peer-stats';
import { PeerStatsParticipant } from 'src/app/models/peer-stats/peer-stats-participant';

// Services
import { DC_TIME_THRESHOLD, DataChannelService } from '../data-channel/data-channel.service';
import { FeatureSwitchService } from 'src/app/services/feature-switch/feature-switch.service';
import { IntervalService } from 'src/app/services/interval/interval.service';
import { IssueDetectionService } from 'src/app/services/issue-detection/issue-detection.service';
import { IssueRemediationService } from 'src/app/services/issue-remediation/issue-remediation.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LoggingChannels, LoggingService } from 'src/app/services/logging/logging.service';
import { ParticipantsListService } from '../participants-list/participants-list.service';
import { SignalingService } from '../signaling/signaling.service';
import { FeatureName, SupportService } from '../support/support.service';
import { VirtualBackgroundService } from '../virtual-background/virtual-background.service';
import { SignalingTransportService } from '../signaling-transport/signaling-transport.service';

// Helpers
import { StatsCollector } from './stats-collector';
import { StatsDisplay } from './stats-display';
import { StatsIssueChecker } from './stats-issue-checker';
import { StatsParser } from './stats-parser';
import { StatsReporter } from './stats-reporter';

export const STATS_INTERVAL = 1000; // ms
const SEND_INTERVAL = 60 * 1000; // ms

declare global {
  interface Window {
    crewdleStats: boolean;
  }
};

@Injectable({
  providedIn: 'root',
})
export class CallStatsService {
  roomId: string;
  userId: string;
  session: any;
  inputDevices: any[];
  constants: any;
  report: PeerStats;
  shareVideoTrackId: string;
  shareAudioTrackId: string;

  private statsCollector: StatsCollector;
  private statsDisplay: StatsDisplay;
  private statsIssueChecker: StatsIssueChecker;
  private statsReporter: StatsReporter;

  constructor(
    private transport: SignalingTransportService,
    private background: VirtualBackgroundService,
    private dataChannel: DataChannelService,
    private featureSwitch: FeatureSwitchService,
    private intervalService: IntervalService,
    private issueDetection: IssueDetectionService,
    private issueRemediation: IssueRemediationService,
    private localStorage: LocalStorageService,
    private logging: LoggingService,
    private participants: ParticipantsListService,
    private platform: PlatformService,
    private signaling: SignalingService,
    private support: SupportService,
  ) {
    this.inputDevices = [];

    const statsParser = new StatsParser();
    this.statsCollector = new StatsCollector(this.participants, statsParser);
    this.statsDisplay = new StatsDisplay(
      this.transport,
      this.background,
      this.dataChannel,
      this.issueRemediation,
      this.participants,
      this.signaling,
    );
    this.statsIssueChecker = new StatsIssueChecker(
      this.issueDetection,
      this.issueRemediation,
      this.participants,
    );
    this.statsReporter = new StatsReporter(
      this.background,
      this.logging,
      this.participants,
    );
  }

  public init(roomId: string, userId: string) {
    this.roomId = roomId;
    this.userId = userId;

    this.logging.log('CallStatsService: init', LoggingChannels.CallStats);

    if (this.featureSwitch.callstats) {
      this.report = new PeerStats();
      this.intervalService.setInterval(
        'callstats-send',
        () => {
          if (this.participants.nbParticipants > 1 && this.report) {
            this.sendStats();
          }
        },
        SEND_INTERVAL
      );
      let isRunning = false;
      this.intervalService.setInterval(
        'callstats-get',
        async () => {
          if (this.participants.nbParticipants > 1 && this.report && !isRunning) {
            isRunning = true;
            const nextParticipant = this.report.nextParticipant;
            const now = Date.now();

            if (
              nextParticipant &&
              this.participants.participants[nextParticipant]?.dataChannel?.readyState === 'open' &&
              (now - this.dataChannel.lasts[nextParticipant]) <= DC_TIME_THRESHOLD &&
              this.participants.participants[nextParticipant]?.pc?.connectionState !== 'failed' &&
              this.participants.participants[nextParticipant]?.pc?.connectionState !== 'disconnected'
            ) {
              this.report.scheduleTime = STATS_INTERVAL;
              if (this.report.scheduleTimestamp) {
                this.report.scheduleTime = now - this.report.scheduleTimestamp;
              }
              this.report.scheduleTimestamp = now;

              this.statsCollector.getStats(
                nextParticipant,
                this.participants.participants[nextParticipant].pc,
                this.report,
                this.userId,
                this.shareVideoTrackId,
                this.shareAudioTrackId,
              ).then(() => {
                this.report.durationTime = Date.now() - now;
                try {
                  this.statsIssueChecker.checkIssues(nextParticipant, this.report, this.userId, this.shareAudioTrackId);
                } catch (err) {}
                isRunning = false;
              }).catch((err) => {
                isRunning = false;
              });
            } else {
              this.report.scheduleTimestamp = now;
              isRunning = false;
            }
          }
          if (this.featureSwitch.showStats && window.crewdleStats) {
            this.participants.participantsArray.forEach((p) => {
              const statsDiv = document.getElementById('video-stats-' + p.id);
              const data = this.getData(p.id);
              if (data && statsDiv) {
                statsDiv.innerHTML = data;
              }
              const homeStatsDiv = document.getElementById('video-stats-home');
              const homeData = this.getHostData();
              if (homeData && homeStatsDiv) {
                homeStatsDiv.innerHTML = homeData;
              }
            });
          }
        },
        STATS_INTERVAL
      );
    }
  }

  public start(
    roomId: string,
    userId: string,
    participantId: string
  ) {
    this.roomId = roomId;
    this.userId = userId;
    this.report.participantsStats[participantId] = new PeerStatsParticipant(participantId);

    this.logging.log('CallStatsService: start', LoggingChannels.CallStats, participantId);
  }

  public updateUser(roomId: string, userId: string) {
    this.roomId = roomId;
    this.userId = userId;
    this.getInputDevices().then(() => {
      this.updateCallStatsUser();
    });
  }

  public stop(id: string) {
    this.logging.log('CallStatsService: stop', LoggingChannels.CallStats, id);
    this.clearStats(id);
  }

  public close() {
    this.logging.log('CallStatsService: close', LoggingChannels.CallStats);
    if (this.featureSwitch.callstats) {
      this.report = null;
      this.intervalService.clearInterval('callstats-get');
      this.intervalService.clearInterval('callstats-send');
    }
  }

  public getHostData(): string {
    return this.statsDisplay.getHostData(this.report);
  }

  public getData(id?: string): string {
    return this.statsDisplay.getData(id, this.report);
  }

  public logAlert(alerts: any) {
    this.logging.logCustomEvent('alert', {
      userId: this.userId,
      roomId: this.roomId,
      alerts,
    });
  }

  public logEvent(
    event: string,
    options?: any,
    roomId?: string,
    userId?: string
  ) {
    this.logging.logCustomEvent('event', {
      userId: this.userId || userId,
      roomId: this.roomId || roomId,
      event,
      options: options || null,
    });
  }

  public getMetricsForTopology(participantId: string): PeerMetrics {
    const metrics = new PeerMetrics(participantId);
    if (this.report?.participantsStats[participantId]) {
      const stats = this.report.participantsStats[participantId];
      metrics.availableBandwidthKbps = stats.outgoingBitrate || 1000;
      metrics.latencyMs = stats.rtt || 100;
    }
    return metrics;
  }

  private sendStats() {
    this.statsReporter.sendStats(this.report, this.userId, this.roomId);
  }

  private clearStats(id: string) {
    if (this.report) {
      if (this.report.participantsStats[id]) {
        delete this.report.participantsStats[id];
      }
      if (this.report.shareStats[id]) {
        delete this.report.shareStats[id];
      }
    }
  }

  private getUserInfo() {
    const navigatorObj: any = navigator;
    const connectionObj: any = navigatorObj.connection;

    if (['slow-2g', '2g', '3g'].includes(connectionObj?.effectiveType)) {
      this.issueDetection.logEvent(CallIssueType.weak_network, null, connectionObj?.effectiveType);
    }

    return {
      userId: this.userId,
      roomId: this.roomId,
      status: 'open',
      version: environment.version,
      mobile: this.platform.is('mobile'),
      tablet: this.platform.is('tablet'),
      inputDevices: this.inputDevices,
      localTime: new Date().toUTCString(),
      networkType: connectionObj?.effectiveType || null,
      downlink: connectionObj?.downlink || null,
      rtt: connectionObj?.rtt || null,
      support: {
        audioOutputSupported: this.support.isFeatureSupported(FeatureName.AudioOutput),
        backgroundSupported: this.support.isFeatureSupported(FeatureName.Backgrounds),
        configSupported: this.support.isFeatureSupported(FeatureName.DeviceConfig),
        recordingSupported: this.support.isFeatureSupported(FeatureName.Recording),
        sharingSupported: this.support.isFeatureSupported(FeatureName.ScreenSharing),
        streamSupported: this.support.isFeatureSupported(FeatureName.MediaStream),
        volumeSupported: this.support.isFeatureSupported(FeatureName.AudioVolume),
        webglSupported: this.support.isFeatureSupported(FeatureName.WebGL),
        webRTCSupported: this.support.isFeatureSupported(FeatureName.WebRTC),
      }
    };
  }

  private updateCallStatsUser() {
    this.logging.logCustomEvent('user', this.getUserInfo());
  }

  private getInputDevices(): Promise<void> {
    const inputs = [];
    const videoInputId = this.localStorage.getItem('videoInputId');
    const audioInputId = this.localStorage.getItem('audioInputId');
    const audioOutputId = this.localStorage.getItem('audioOutputId');
    return navigator.mediaDevices.enumerateDevices().then((devices) => {
      devices.forEach((device) => {
        let selected = '';
        if (
          device.kind === 'videoinput' && device.deviceId === videoInputId ||
          device.kind === 'audioinput' && device.deviceId === audioInputId ||
          device.kind === 'audiooutput' && device.deviceId === audioOutputId
        ) {
          selected = '[SELECTED] ';
        }
        inputs.push(`${selected}${device.kind} : ${device.label}`);
      });
      this.inputDevices = inputs;
    });
  }
}
